import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withStyles, Grid } from '@material-ui/core';
import { FHEContainer } from 'data/components';
import { Toastr } from 'data/common';
import UserCard from '../components/UserCard';
import { getHomeUsers, deleteHomeUser, addHomeUser, editHomeUser, resetPassword } from '../../../data/Homes/actions';
import selectors from '../selectors';

class HomeUsers extends PureComponent {
    static propTypes = {
        deleteHomeUser: PropTypes.func.isRequired,
        getHomeUsers: PropTypes.func.isRequired,
        addHomeUser: PropTypes.func.isRequired,
        resetPassword: PropTypes.func.isRequired,
        editHomeUser: PropTypes.func.isRequired,
        homeId: PropTypes.string.isRequired,
        profile: PropTypes.object.isRequired,
        classes: PropTypes.object.isRequired,
        homeUsers: PropTypes.object.isRequired,
        isActive: PropTypes.bool
    };

    static defaultProps = {
        isActive: true
    };

    constructor(props) {
        super(props);
        this.state = {
            actionDisabled: false,
            loading: props.isActive,
            needGetData: props.isActive,
            fetching: false,
            initialized: false,
            error: null
        };
    }

    static getDerivedStateFromProps(props, state) {
        const { needGetData, loading, error, fetching, initialized } = state;
        const { isActive } = props;

        if (isActive && !needGetData && !loading && !error && !fetching && !initialized) return { needGetData: true, loading: true };
        return null;
    }

    componentDidMount() {
        const { isActive } = this.props;
        if (isActive) this.getHomeUsers();
    }

    componentDidUpdate() {
        const { isActive } = this.props;
        const { needGetData, fetching, error } = this.state;

        if (isActive && needGetData && !fetching && !error) {
            this.getHomeUsers();
        }
    }

    getHomeUsers = () => {
        const { getHomeUsers, homeId } = this.props;
        this.state.fetching = true;
        getHomeUsers(homeId)
            .then(() => {
                this.setState({
                    error: null,
                    loading: false,
                    fetching: false,
                    needGetData: false,
                    initialized: true
                });
            })
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(message);
                this.setState({ error: message, loading: false, needGetData: false, fetching: false });
            });
    };

    retryGetData = () => {
        this.setState({ error: null, loading: true, needGetData: true });
    };

    toggleStateActionDisabled = state => {
        this.setState({ actionDisabled: state });
    };

    addHomeUser = userForm => {
        const { addHomeUser, homeId } = this.props;

        return addHomeUser(homeId, userForm)
            .then(() => {
                Toastr.success(Translator.translate('generic.user.actions.addUserSuccess', userForm.lastname, userForm.firstname));
            })
            .catch(err => {
                Toastr.error(Translator.translate('generic.user.actions.addUserError', userForm.lastname, userForm.firstname));
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };
    editHomeUser = (userId, userForm) => {
        const { editHomeUser, homeId } = this.props;

        return editHomeUser(homeId, userId, userForm)
            .then(() => {
                Toastr.success(Translator.translate('generic.user.actions.editUserSuccess', userForm.lastname, userForm.firstname));
            })
            .catch(err => {
                Toastr.error(Translator.translate('generic.user.actions.editUserError', userForm.lastname, userForm.firstname));
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };
    deleteHomeUser = userId => {
        const { deleteHomeUser, homeId } = this.props;

        return deleteHomeUser(homeId, userId)
            .then(() => {
                Toastr.success(Translator.translate('generic.user.actions.removeSuccess'));
            })
            .catch(err => {
                Toastr.error(Translator.translate('generic.user.actions.removeError'), err);
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };
    resetPassword = userId => {
        const { resetPassword, homeId } = this.props;
        return resetPassword(userId, homeId)
            .then(data => {
                return data;
            })
            .catch(err => {
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    renderUserList = () => {
        const { actionDisabled } = this.state;
        const { homeUsers, profile } = this.props;

        const userRights = profile.getIn(['user_rights', 'fhe'], []);
        const users = [];
        homeUsers.map((user, userId) => {
            users.push(
                <Grid key={userId} container item md={12} lg={6} xl={4}>
                    <UserCard
                        user={user}
                        userId={parseInt(userId)}
                        editUserFunc={this.editHomeUser}
                        deleteUserFunc={this.deleteHomeUser}
                        resetPasswordFunc={this.resetPassword}
                        actionDisabled={actionDisabled}
                        resetUserPasswordRight={profile.get('admin') || userRights.includes(`homeResetUserPasswordFhe`)}
                        deleteUserRight={profile.get('admin') || userRights.includes(`homeDeleteUserFhe`)}
                        editUserRight={profile.get('admin') || userRights.includes(`homeEditUserFhe`)}
                        toggleActions={this.toggleStateActionDisabled}
                    />
                </Grid>
            );
        });
        if (users.length < 4 && (profile.get('admin') || userRights.includes(`homeAddUserFhe`))) {
            users.push(
                <Grid key="cardAdd" container item md={12} lg={6} xl={4}>
                    <UserCard modeAdd addUserFunc={this.addHomeUser} />
                </Grid>
            );
        }
        if (users.length === 0) return <div>{Translator.translate('generic.user.empty')}</div>;
        return (
            <Grid container spacing={2} justify={users.length === 1 ? 'center' : 'flex-start'}>
                {users}
            </Grid>
        );
    };
    render() {
        const { error, loading } = this.state;
        const { classes } = this.props;

        return (
            <FHEContainer
                hasNoClassContainer
                loading={loading}
                error={error}
                classContainer={classes.container}
                onClickFunc={this.retryGetData}
                onRender={this.renderUserList}
            />
        );
    }
}

function mapStateToProps(state, props) {
    const homeId = props.match.params.homeId;
    return {
        profile: selectors.getProfile(state),
        homeUsers: selectors.getUsersByHomeId(state, homeId),
        homeId
    };
}

const styles = () => ({
    container: {
        margin: 8
    },
    loadingContainer: {
        textAlign: 'center',
        margin: 20
    }
});

export default withRouter(
    connect(
        mapStateToProps,
        dispatch => bindActionCreators({ getHomeUsers, deleteHomeUser, addHomeUser, editHomeUser, resetPassword }, dispatch)
    )(withStyles(styles, { withTheme: true })(HomeUsers))
);



// WEBPACK FOOTER //
// ./environments/fhe/scenes/Home/sections/HomeUsers.js